import { BASE_URL } from './baseURL';
import { getClientSource } from '../utils/getClientSource';

// API endpoints
const ENDPOINTS = {
  DOMESTIC_QUOTE: '/domestic/quote',
  DOMESTIC_UPDATE: '/domestic/update'
};

// Utility functions for data transformation
export const convertToUnixTimestamp = (dateString) => {
  if (!dateString) return null;
  return Math.floor(new Date(dateString).getTime() / 1000);
};

export const getCurrentUnixTimestamp = () => {
  return Math.floor(Date.now() / 1000);
};

// Generic API call function with error handling
const makeAPICall = async (endpoint, data, options = {}) => {
  const { timeout = 30000 } = options; // 30 second timeout

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log(`=== Making Domestic Helper API call ===`);
    console.log(`Endpoint: ${BASE_URL}${endpoint}`);
    console.log(`Payload:`, JSON.stringify(data, null, 2));
    console.log(`Timeout: ${timeout}ms`);

    const headers = {
      'Content-Type': 'application/json',
    };

    const startTime = Date.now();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const endTime = Date.now();
    console.log(`API call completed in ${endTime - startTime}ms`);
    console.log(`Response status: ${response.status}`);

    if (!response.ok) {
      console.error(`API Error Response:`, response.status, response.statusText);
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error(`Error data:`, errorData);
        if (errorData.ErrorMessageEn) {
          errorMessage = errorData.ErrorMessageEn;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Use default error message if can't parse response
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`=== API Success Response ===`);
    console.log(`TotalPremium: ${result.TotalPremium}`);
    console.log(`Premium: ${result.Premium}`);
    console.log(`Tax: ${result.Tax}`);
    console.log(`PolicyNumber: ${result.PolicyNumber}`);
    console.log(`ApplicationNumber: ${result.ApplicationNumber}`);

    return { success: true, data: result };

  } catch (error) {
    clearTimeout(timeoutId);

    console.error(`=== API Error ===`);
    console.error(`Error name: ${error.name}`);
    console.error(`Error message: ${error.message}`);
    console.error(`Full error:`, error);

    if (error.name === 'AbortError') {
      console.error('Request was aborted due to timeout');
      return {
        success: false,
        error: `Request timed out after ${timeout}ms`,
        isTimeout: true
      };
    }

    const isNetworkError = !navigator.onLine || error.name === 'TypeError' || error.message.includes('fetch');
    const isServerError = error.message.includes('server issues') || error.message.includes('500');

    return {
      success: false,
      error: error.message || 'API call failed',
      isNetworkError: isNetworkError,
      isServerError: isServerError,
      isTimeout: error.name === 'AbortError'
    };
  }
};

/**
 * Transform domestic helper form data to API request format for quote
 * @param {Object} helperData - Helper form data
 * @param {Object} applicationData - Application details data
 * @param {string} partyCode - User's party code
 * @returns {Object} API request payload
 */
const transformDomesticHelperFormData = (helperData, applicationData, partyCode = 'FALCON', proposerInfo = null) => {
  console.log('=== TRANSFORMING DOMESTIC HELPER FORM DATA ===');
  console.log('Helper data:', helperData);
  console.log('Application data:', applicationData);
  console.log('Party code:', partyCode);
  console.log('Proposer info:', proposerInfo);

  // Convert date of birth to Unix timestamp
  let dateOfBirth = null;
  if (helperData?.dateOfBirth?.day && helperData?.dateOfBirth?.month && helperData?.dateOfBirth?.year) {
    const { day, month, year } = helperData.dateOfBirth;
    const dobDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    dateOfBirth = Math.floor(dobDate.getTime() / 1000);
  }

  // Get nationality code - need to extract number from nationality string
  // Assuming nationality format from COUNTRY_SELECT_DATA is like "14|Bahrain"
  let nationalityCode = null;
  if (helperData?.nationality) {
    const nationalityStr = helperData.nationality.toString();
    // If nationality contains a pipe, extract the number
    if (nationalityStr.includes('|')) {
      nationalityCode = parseInt(nationalityStr.split('|')[0]);
    } else {
      // Try to parse as number directly
      const parsed = parseInt(nationalityStr);
      if (!isNaN(parsed)) {
        nationalityCode = parsed;
      }
    }
  }

  console.log('Nationality parsing:', {
    original: helperData?.nationality,
    parsed: nationalityCode
  });

  // Map gender to M/F format
  let gender = '';
  if (helperData?.gender) {
    const genderLower = helperData.gender.toLowerCase();
    if (genderLower === 'male' || genderLower === 'm') {
      gender = 'M';
    } else if (genderLower === 'female' || genderLower === 'f') {
      gender = 'F';
    }
  }

  // Map application type
  let applicationType = 'new';
  if (applicationData?.applicationType) {
    const appTypeLower = applicationData.applicationType.toLowerCase();
    if (appTypeLower.includes('renewal')) {
      applicationType = 'renewal';
    } else if (appTypeLower.includes('interim')) {
      applicationType = 'interim';
    } else {
      applicationType = 'new';
    }
  }

  // Get period (1 or 2 years)
  let period = 2; // Default to 2 years
  if (applicationData?.policyPeriod) {
    if (applicationData.policyPeriod.includes('1')) {
      period = 1;
    } else if (applicationData.policyPeriod.includes('2')) {
      period = 2;
    }
  }

  // Get sum insured from application data (Limit for absence of work).
  //
  // The tab writes "bhd_500" / "bhd_1000" / "bhd_1500", and the API wants the
  // bare amount as a string. Read the number out rather than testing for
  // substrings: "bhd_1500" contains "500", so a shortest-first chain of
  // .includes() answered 500 for a customer who had picked BHD 1,500 —
  // under-insuring the policy and pricing it at the wrong premium. Only 1,500
  // was affected; "bhd_1000" happens not to contain any of the other amounts.
  const ABSENCE_LIMITS = [500, 1000, 1500];
  const parseAbsenceLimit = (value) => {
    // Tolerates "bhd_1500", "BHD 1,500", "1500.000" and a plain 1500.
    const amount = Number.parseFloat(String(value ?? "").replace(/[^\d.]/g, ""));
    return ABSENCE_LIMITS.includes(amount) ? String(amount) : null;
  };

  // The two older field names stay as fallbacks, in the same order of
  // preference they had before.
  const sumInsured = parseAbsenceLimit(
    applicationData?.limitForAbsence ||
      applicationData?.limitForAbsenceOfWork ||
      applicationData?.sumInsured
  );

  console.log('Sum Insured parsing:', {
    limitForAbsence: applicationData?.limitForAbsence,
    limitForAbsenceOfWork: applicationData?.limitForAbsenceOfWork,
    sumInsured: applicationData?.sumInsured,
    parsed: sumInsured
  });

  // Map employment type to API values (Direct or Office)
  let employmentType = 'Office'; // Default
  if (applicationData?.employmentType) {
    const empTypeLower = applicationData.employmentType.toLowerCase();
    if (empTypeLower.includes('direct')) {
      employmentType = 'Direct';
    } else if (empTypeLower.includes('office')) {
      employmentType = 'Office';
    }
  }

  // Calculate dates (FromDate and ToDate)
  const fromDate = getCurrentUnixTimestamp();
  const toDate = fromDate + (period * 365 * 24 * 60 * 60); // Add years in seconds

  const payload = {
    ApplicationType: applicationType,
    Channel: getClientSource(),
    CPR: helperData?.cprNumber || '',
    DateOfBirth: dateOfBirth,
    EmploymentType: employmentType,
    Gnender: gender,
    Name: helperData?.fullName || '',
    Nationality: nationalityCode,
    PartyCode: partyCode,
    PassportNumber: helperData?.passportNumber || '',
    Period: period,
    SumInsured: sumInsured,
    ToDate: toDate,
    FromDate: fromDate
  };

  // Carry the proposer's (customer's) start-page basic info so the
  // domestic-helper quote payload does not orphan the customer details.
  // NOTE: backend field names for proposer are not formally documented in
  // ENDPOINTS.md - using conventional Proposer* prefixed keys; backend
  // can ignore unknown fields safely.
  if (proposerInfo) {
    let proposerDob = null;
    if (proposerInfo.birthDate) {
      const [y, m, d] = proposerInfo.birthDate.split('-');
      if (y && m && d) {
        proposerDob = Math.floor(
          new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).getTime() / 1000
        );
      }
    }
    if (proposerInfo.fullName) payload.ProposerName = proposerInfo.fullName;
    if (proposerInfo.email) payload.ProposerEmail = proposerInfo.email;
    if (proposerInfo.contactNumber) payload.ProposerPhone = proposerInfo.contactNumber;
    if (proposerInfo.cpr) payload.ProposerCPR = proposerInfo.cpr;
    if (proposerDob) payload.ProposerDateOfBirth = proposerDob;
  }

  // Note: domestic helper quote payload does not carry the customer CPR
  // (only the helper's CPR) - keep FALCON as the guest fallback.

  console.log('=== FINAL DOMESTIC HELPER API PAYLOAD ===');
  console.log(JSON.stringify(payload, null, 2));
  console.log('=== END PAYLOAD ===');

  return payload;
};

/**
 * Transform domestic helper API response to match premium structure
 * @param {Object} apiResponse - API response
 * @returns {Object} Transformed premium data
 */
const transformDomesticHelperResponse = (apiResponse) => {
  console.log('=== Transforming Domestic Helper API response ===');
  console.log('Raw API response:', apiResponse);

  const transformed = {
    // Main pricing info
    totalPremium: parseFloat(apiResponse.TotalPremium || 0),
    premium: parseFloat(apiResponse.Premium || 0),
    tax: parseFloat(apiResponse.Tax || 0),
    sumInsured: parseFloat(apiResponse.SumInsured || 0),

    // Policy/Quote info
    policyNumber: apiResponse.PolicyNumber || '',
    applicationNumber: apiResponse.ApplicationNumber || '',
    quoteNumber: apiResponse.QuoteDetails?.QuoteNumber || apiResponse.PolicyNumber || '',

    // Additional details from QuoteDetails
    productName: apiResponse.QuoteDetails?.ProductName || '',
    productCode: apiResponse.QuoteDetails?.ProductCode || '',

    // Dates
    fromDate: apiResponse.QuoteDetails?.FromDate,
    toDate: apiResponse.QuoteDetails?.ToDate,
    fromDateStr: apiResponse.QuoteDetails?.FromDateStr,
    toDateStr: apiResponse.QuoteDetails?.ToDateStr,

    // Coverage details
    covers: apiResponse.QuoteDetails?.Covers || [],

    // Domestic helper specific details
    helperName: apiResponse.QuoteDetails?.Name || '',
    passportNumber: apiResponse.QuoteDetails?.PassportNumber || '',
    nationality: apiResponse.QuoteDetails?.Nationality || '',
    gender: apiResponse.QuoteDetails?.Gender || '',
    dateOfBirth: apiResponse.QuoteDetails?.DateOfBirth || '',
    cpr: apiResponse.QuoteDetails?.CPR || '',
    employmentType: apiResponse.QuoteDetails?.EmploymentType || '',
    applicationType: apiResponse.QuoteDetails?.ApplicationType || '',
    period: apiResponse.QuoteDetails?.Period || 0,

    // Additional pricing details
    detailPremium: parseFloat(apiResponse.QuoteDetails?.Premium || 0),
    detailTax: parseFloat(apiResponse.QuoteDetails?.Tax || 0),
    oldPremium: parseFloat(apiResponse.QuoteDetails?.OldPremium || 0),

    // Business details
    partyCode: apiResponse.QuoteDetails?.PartyCode || '',
    sourceOfBusiness: apiResponse.QuoteDetails?.SourceOfBusiness || '',
    statusCode: apiResponse.QuoteDetails?.StatusCode || '',
    modeOfBusiness: apiResponse.QuoteDetails?.ModeOfBusiness || '',

    // Flags
    isRenewable: apiResponse.QuoteDetails?.IsRenewable || false,
    isRenewed: apiResponse.QuoteDetails?.IsRenewed || false,

    // Error handling
    errorMessage: apiResponse.QuoteDetails?.ErrorMessage || '',
    errorCode: apiResponse.ErrorCode || 0,
    points: apiResponse.Points || 0
  };

  console.log('Transformed domestic helper response:', {
    totalPremium: transformed.totalPremium,
    premium: transformed.premium,
    tax: transformed.tax,
    quoteNumber: transformed.quoteNumber,
    applicationNumber: transformed.applicationNumber
  });

  return transformed;
};

/**
 * Get domestic helper insurance quote
 * @param {Object} helperData - Helper form data
 * @param {Object} applicationData - Application details data
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Quote response
 */
export const getDomesticHelperQuote = async (helperData, applicationData, partyCode = 'FALCON', options = {}, proposerInfo = null) => {
  try {
    const { timeout = 30000 } = options;

    // Validate required data
    if (!helperData.passportNumber || !helperData.fullName) {
      return {
        success: false,
        error: 'Passport number and full name are required',
        data: null
      };
    }

    if (!helperData.nationality || !helperData.gender) {
      return {
        success: false,
        error: 'Nationality and gender are required',
        data: null
      };
    }

    if (!helperData.dateOfBirth?.day || !helperData.dateOfBirth?.month || !helperData.dateOfBirth?.year) {
      return {
        success: false,
        error: 'Date of birth is required',
        data: null
      };
    }

    // Transform form data
    const requestPayload = transformDomesticHelperFormData(helperData, applicationData, partyCode, proposerInfo);
    console.log('=== Domestic Helper Quote Request ===');
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.DOMESTIC_QUOTE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformDomesticHelperResponse(result.data);
      console.log('=== Domestic Helper Quote Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data // Keep original for debugging
      };
    }

    console.error('=== Domestic Helper Quote Failed ===');
    console.error('Error:', result.error);
    console.error('Is timeout:', result.isTimeout);
    console.error('Is network error:', result.isNetworkError);

    return {
      success: false,
      error: result.error || 'Failed to get domestic helper quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Domestic helper quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get domestic helper quote',
      data: null
    };
  }
};

/**
 * Update domestic helper insurance quote
 * @param {Object} helperData - Helper form data
 * @param {Object} applicationData - Application details data
 * @param {string} applicationNumber - Application number from initial quote
 * @param {string} partyCode - User's party code
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Update response
 */
export const updateDomesticHelperQuote = async (helperData, applicationData, applicationNumber, partyCode = 'FALCON', options = {}, proposerInfo = null) => {
  try {
    const { timeout = 30000 } = options;

    if (!applicationNumber) {
      return {
        success: false,
        error: 'Application number is required for update',
        data: null
      };
    }

    // Transform form data (same as quote)
    const requestPayload = transformDomesticHelperFormData(helperData, applicationData, partyCode, proposerInfo);

    // Add ApplicationNumber to payload for update
    requestPayload.ApplicationNumber = applicationNumber;

    console.log('=== Domestic Helper Update Request ===');
    console.log('Request payload:', requestPayload);
    console.log('Timeout:', timeout);

    const result = await makeAPICall(ENDPOINTS.DOMESTIC_UPDATE, requestPayload, {
      timeout: timeout
    });

    if (result.success) {
      const transformedData = transformDomesticHelperResponse(result.data);
      console.log('=== Domestic Helper Update Success ===');
      console.log('Transformed data:', transformedData);

      return {
        success: true,
        data: transformedData,
        raw: result.data
      };
    }

    console.error('=== Domestic Helper Update Failed ===');
    console.error('Error:', result.error);

    return {
      success: false,
      error: result.error || 'Failed to update domestic helper quote',
      data: null,
      isNetworkError: result.isNetworkError,
      isServerError: result.isServerError,
      isTimeout: result.isTimeout
    };

  } catch (error) {
    console.error('Domestic helper update exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to update domestic helper quote',
      data: null
    };
  }
};

export default {
  getDomesticHelperQuote,
  updateDomesticHelperQuote
};
